<script setup>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { XMarkIcon, BanknotesIcon } from '@heroicons/vue/24/outline'
import { useQrCodeModalStore } from '@/stores/qrCodeModalStore'
import { useTranslationStore } from '@/stores/translationStore'
import { computed, ref } from 'vue'

const qrCodeModalStore = useQrCodeModalStore()
const translationStore = useTranslationStore()

const hasQrCode = computed(() => qrCodeModalStore.qrCodeDataUrl !== null)
const hasError = computed(() => qrCodeModalStore.error !== null)
const bankDetailsModalOpen = ref(false)

// Function to parse EPC string into bank details
const parseEpcString = (epcString) => {
    if (!epcString) return null
    
    try {
        // Handle different EPC formats
        if (epcString.includes('|')) {
            // Format: BIC|Name|IBAN|Amount|Currency|Reference|Description
            const parts = epcString.split('|')
            if (parts.length < 7) return null
            
            return {
                bic: parts[0] || '',
                beneficiaryName: parts[1] || '',
                iban: parts[2] || '',
                amount: parts[3] || '',
                currency: parts[4] || 'EUR',
                reference: parts[5] || '',
                description: parts[6] || ''
            }
        } else {
            // Standard EPC format (newline separated)
            const lines = epcString.split('\n')
            if (lines.length < 11) return null
            
            return {
                bic: lines[4] || '',           // Line 5: BIC
                beneficiaryName: lines[5] || '', // Line 6: Beneficiary name
                iban: lines[6] || '',          // Line 7: IBAN
                amount: lines[7] || '',        // Line 8: Amount
                currency: 'EUR',               // Always EUR for EPC
                reference: lines[9] || '',     // Line 10: Structured reference
                description: lines[10] || ''   // Line 11: Unstructured remittance info
            }
        }
    } catch (error) {
        console.error('Error parsing EPC string:', error)
        return null
    }
}

const bankDetails = computed(() => {
    return parseEpcString(qrCodeModalStore.epcString)
})

const formatAmount = (amount) => {
    if (!amount) return ''
    const numAmount = parseFloat(amount)
    return isNaN(numAmount) ? amount : `€ ${numAmount.toFixed(2).replace('.', ',')}`
}

const openBankDetailsModal = () => {
    bankDetailsModalOpen.value = true
}

const closeBankDetailsModal = () => {
    bankDetailsModalOpen.value = false
}
</script>

<template>
    <!-- Bank Details Modal -->
    <TransitionRoot as="template" :show="bankDetailsModalOpen">
        <Dialog as="div" class="relative z-[10002]">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-[10002] w-screen overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel class="relative transform overflow-hidden rounded-lg text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg mx-12 bg-mc-primary">
                            <button @click="closeBankDetailsModal" class="right-5 top-5 absolute outline-none z-10">
                                <XMarkIcon class="h-10 w-10 text-white" aria-hidden="true" />
                            </button>
                            
                            <div class="flex flex-col text-white w-full gap-2 py-4">
                                <div class="px-6">
                                    <div class="flex items-center mb-6">
                                        <BanknotesIcon class="h-8 w-8 text-white mr-3" />
                                        <h3 class="text-xl font-semibold text-white">
                                            {{ translationStore.t('qr.modal.bank_details') }}
                                        </h3>
                                    </div>
                                    
                                    <div v-if="bankDetails" class="space-y-1">
                                        <div class="text-center text-gray-200 mb-6 font-medium">{{ translationStore.t('qr.modal.manual_transfer') }}</div>
                                        
                                        <div class="space-y-4">
                                            <div v-if="bankDetails.beneficiaryName">
                                                <label class="text-white">{{ translationStore.t('qr.modal.beneficiary') }}</label>
                                                <input type="text" :value="bankDetails.beneficiaryName" readonly class="maxi-input bg-gray-100" />
                                            </div>
                                            
                                            <div v-if="bankDetails.iban">
                                                <label class="text-white">{{ translationStore.t('qr.modal.iban') }}</label>
                                                <input type="text" :value="bankDetails.iban" readonly class="maxi-input bg-gray-100 font-mono" />
                                            </div>
                                            
                                            <div class="grid grid-cols-2 gap-4">
                                                <div v-if="bankDetails.amount">
                                                    <label class="text-white">{{ translationStore.t('qr.modal.amount') }}</label>
                                                    <input type="text" :value="formatAmount(bankDetails.amount)" readonly class="maxi-input bg-gray-100 font-bold" />
                                                </div>
                                                <div v-if="bankDetails.bic">
                                                    <label class="text-white">{{ translationStore.t('qr.modal.bank_name') }}</label>
                                                    <input type="text" :value="bankDetails.bic" readonly class="maxi-input bg-gray-100" />
                                                </div>
                                            </div>
                                            
                                            <div v-if="bankDetails.reference">
                                                <label class="text-white">{{ translationStore.t('qr.modal.reference') }}</label>
                                                <input type="text" :value="bankDetails.reference" readonly class="maxi-input bg-gray-100 font-mono" />
                                            </div>
                                            
                                            <div v-if="bankDetails.description">
                                                <label class="text-white">{{ translationStore.t('qr.modal.description') }}</label>
                                                <textarea :value="bankDetails.description" readonly rows="2" class="maxi-input bg-gray-100"></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="px-6 p-1 flex gap-4">
                                    <button 
                                        type="button" 
                                        @click="closeBankDetailsModal"
                                        class="w-full maxi-button"
                                    >
                                        {{ translationStore.t('qr.modal.close') }}
                                    </button>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>

    <!-- Main QR Code Modal -->
    <TransitionRoot as="template" :show="qrCodeModalStore.modalOpen">
        <Dialog as="div" class="relative z-[10001]">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-[10001] w-screen overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel class="relative transform overflow-hidden rounded-lg text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl mx-12 bg-mc-primary">
                            <button @click="qrCodeModalStore.closeModal" class="right-5 top-5 absolute outline-none z-10">
                                <XMarkIcon class="h-10 w-10 text-white" aria-hidden="true" />
                            </button>
                            
                            <div class="flex flex-col text-white w-full gap-2 py-3">
                                <div class="px-6">
                                    <h3 class="text-lg font-medium leading-6 text-white mb-3 text-center">
                                        {{ translationStore.t('qr.modal.title') }}
                                    </h3>
                                    
                                    <!-- Loading State -->
                                    <div v-if="qrCodeModalStore.loading" class="flex flex-col items-center justify-center py-8">
                                        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                                        <p class="mt-4 text-gray-200">{{ translationStore.t('qr.modal.loading') }}</p>
                                    </div>
                                    
                                    <!-- Error State -->
                                    <div v-else-if="hasError" class="flex flex-col items-center justify-center py-8">
                                        <div class="bg-red-100 border border-red-600 rounded-lg text-red-800 p-4">
                                            <p class="font-bold">{{ translationStore.t('qr.modal.error') }}</p>
                                            <p class="text-sm">{{ qrCodeModalStore.error }}</p>
                                        </div>
                                    </div>
                                    
                                    <!-- QR Code Display -->
                                    <div v-else-if="hasQrCode" class="py-4">
                                        <!-- QR Code Section -->
                                        <div class="flex flex-col items-center">
                                            <div class="bg-white p-3 rounded-lg shadow-md">
                                                <img 
                                                    :src="qrCodeModalStore.qrCodeDataUrl" 
                                                    alt="QR Code for Payment"
                                                    class="w-48 h-48 object-contain"
                                                />
                                            </div>
                                            
                                            <!-- Payment Amount -->
                                            <div v-if="bankDetails?.amount" class="mt-4 text-center">
                                                <div class="inline-block bg-mc-tertiary px-4 py-3 rounded-lg border border-white/20">
                                                    <div class="text-2xl font-bold text-white">
                                                        {{ formatAmount(bankDetails.amount) }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                    </div>
                                </div>
                                
                                <div class="px-6 pt-2 pb-3 flex gap-4">
                                    <button 
                                        v-if="bankDetails"
                                        @click="openBankDetailsModal"
                                        class="w-full maxi-button bg-mc-tertiary hover:bg-mc-tertiary/80 border border-white/20 flex items-center justify-center"
                                    >
                                        <BanknotesIcon class="h-5 w-5 mr-2" />
                                        {{ translationStore.t('qr.modal.bank_details') }}
                                    </button>
                                    <button 
                                        type="button" 
                                        @click="qrCodeModalStore.closeModal"
                                        class="w-full maxi-button"
                                    >
                                        {{ translationStore.t('qr.modal.close') }}
                                    </button>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<style scoped>
/* Additional styling if needed */
</style> 